// Reports types and enums
import { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction } from 'react';
import dayjs from 'services/dayjs';
import {
  Dashboard,
  DashboardDataType,
  FilterFragment as Filter,
  FilterInput,
  FilterType,
  ReportScreenInput,
  ReportsDashboardInput,
} from 'generated/graphql';

const REPORTS_DASHBOARD_URL_KEYS = [
  'start',
  'end',
  'search',
  'shipmentReference',
  'printerFriendly',
  'filters',
] as const;

export const DEBOUNCE_TIME = 800;

export interface ReportsDashboardState {
  startDate: Dayjs;
  endDate: Dayjs;
  packingDate: Dayjs;
  searchValue: string;
  filteredSearchValue: string;
  filters: FilterInput[];
  shipmentReference: string;
  printerFriendly: boolean;
  showLinkingError: boolean;
  openTechnoTermsDialog: boolean;
}

export interface ReportsDashboardContextProps {
  /** Combined dashboard state; pass reportsDashboardState and setReportsDashboardState to child providers. */
  reportsDashboardState: ReportsDashboardState;
  setReportsDashboardState: Dispatch<SetStateAction<ReportsDashboardState>>;
  /** Shared input for dashboard tab providers (Pricing, Inventory, Chart, Table). */
  reportsDashboardQueryInput: ReportScreenInput;
  reportsDashboardNewQueryInput: ReportsDashboardInput;
}

export interface ReportsDashboardFiltersContextProps {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  productFilters: Filter[];
  setProductFilters: (productFilters: Filter[]) => void;
  handleFilter: (value?: string, filterInput?: FilterInput[]) => void;
  handleFiltersApplied: (filterInput: FilterInput[], searchValue?: string) => void;
  handleSearch: (value: string) => void;
  onSearchChange: (value: string) => void;
}

export enum WeekToMonthOptions {
  WEEK_TO_DATE = 'Last 7 days',
  WEEK_TO_DATE_MOBILE = 'Last 7d',
  MONTH_TO_DATE = 'Last 30 days',
  MONTH_TO_DATE_MOBILE = 'Last 30d',
}

export enum ViewOptions {
  GRAPH = 'Graph',
  TABLE = 'Table',
}

/** Normalizes query values that may include Go `strconv.Quote` or extra escaping (`\"true\"`). */
export const normalizeExportQueryValue = (raw: string): string => raw.replace(/\\"/g, '"').replace(/\\/g, '');

export const parsePrinterFriendlyParam = (raw: string | null | undefined): boolean => {
  if (raw == null || raw === '') {
    return false;
  }
  const normalized = normalizeExportQueryValue(raw).replace(/"/g, '').trim().toLowerCase();
  return normalized === 'true';
};

export const hasReportsDashboardUrlParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return REPORTS_DASHBOARD_URL_KEYS.some((key) => params.has(key));
};

const isFilterInput = (value: unknown): value is FilterInput => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as FilterInput;
  return (
    typeof candidate.type === 'string' &&
    Object.values(FilterType).includes(candidate.type as FilterType) &&
    Array.isArray(candidate.selectedIDs) &&
    candidate.selectedIDs.every((id) => typeof id === 'string' || typeof id === 'number')
  );
};

export const parseFiltersFromSearchParam = (raw: string | null | undefined): FilterInput[] | undefined => {
  if (!raw) {
    return undefined;
  }

  try {
    const jsonText = normalizeExportQueryValue(raw);
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      parsed = JSON.parse(decodeURIComponent(jsonText));
    }
    if (!Array.isArray(parsed)) {
      return undefined;
    }

    const filters = parsed.filter(isFilterInput).map((filter) => ({
      type: filter.type,
      selectedIDs: filter.selectedIDs.map(String),
    }));

    return filters.length > 0 ? filters : undefined;
  } catch {
    return undefined;
  }
};

export const mergeFiltersWithDefaults = (parsed: FilterInput[]): FilterInput[] => {
  const byType = new Map(parsed.map((filter) => [filter.type, filter]));
  return Object.values(FilterType).map((type) => byType.get(type) ?? { type, selectedIDs: [] });
};

export const parseReportsDashboardStateFromSearch = (search: string): Partial<ReportsDashboardState> => {
  const params = new URLSearchParams(search);
  const partial: Partial<ReportsDashboardState> = {};

  const start = params.get('start');
  if (start && dayjs(start).isValid()) {
    partial.startDate = dayjs(start);
  }

  const end = params.get('end');
  if (end && dayjs(end).isValid()) {
    partial.endDate = dayjs(end);
  }

  if (params.has('search')) {
    const searchParam = params.get('search') ?? '';
    partial.searchValue = searchParam;
    partial.filteredSearchValue = searchParam;
  }

  if (params.has('shipmentReference')) {
    partial.shipmentReference = params.get('shipmentReference') ?? '';
  }

  if (params.has('printerFriendly')) {
    partial.printerFriendly = parsePrinterFriendlyParam(params.get('printerFriendly'));
  }

  const parsedFilters = parseFiltersFromSearchParam(params.get('filters'));
  if (parsedFilters) {
    partial.filters = mergeFiltersWithDefaults(parsedFilters);
  }

  return partial;
};

export const isReportsPdfExport = (search: string): boolean =>
  parsePrinterFriendlyParam(new URLSearchParams(search).get('printerFriendly'));

export const findDashboardTabData = <T>(
  dashboards: Dashboard[] | undefined,
  dataType: DashboardDataType,
): T | undefined => {
  if (!dashboards) {
    return undefined;
  }

  for (const dashboard of dashboards) {
    const tab = dashboard.tabs.find((tab) => tab.dataType === dataType);
    if (tab?.data) {
      return tab.data as T;
    }
  }

  return undefined;
};
